import { createContext } from 'react';
import type { AlertViewModel } from '../../api/adapters';
import type { LocateFocus, LocateFrom } from '../locate';
import type { ToastHold, ToastItem } from './constants';

/**
 * Identifier for the drawer/secondary panel an alert should open
 * after navigation. Used by the bell + toast click handler to route
 * the operator to the right place without each consumer needing to
 * parse the alert's deep_link field itself.
 */
export type AlertDeepLinkKind =
  // `robotName` is always the BARE name -- the one every robot list and
  // drawer matches on -- even when the link was fleet-qualified
  // (`robot:<fleet>/<name>`, the locate-link form). The fleet, when the
  // link carried one, is kept beside it rather than inside it.
  | { kind: 'robot'; robotName: string; fleetName?: string }
  | { kind: 'task'; taskId: string }
  | { kind: 'door'; doorName: string }
  | { kind: 'lift'; liftName: string };

/** A bell-rendered alert with read state folded in. Lives next to
 *  `AlertViewModel` rather than mutating it so the upstream type
 *  remains a pure data shape; consumers like the bell dropdown get a
 *  single read-only object per row.
 *
 *  There is deliberately no archive state any more: the bell's old
 *  localStorage-only archive HID an alert per-browser while the same
 *  row stayed open on the Alerts tab, the badge, and every other
 *  operator's screen — two contradictory views of one server row.
 *  Now that resolve and per-device mutes actually work server-side,
 *  those are the ways an alert leaves the bell for everyone. */
export interface BellAlertItem extends AlertViewModel {
  isRead: boolean;
}

/** Fields that change frequently (~10 Hz while alerts tick) and
 *  that only bell / toast consumers care about. Splitting the
 *  context in two means a consumer like `TopBar` that only reads
 *  the stable slice won't re-render on every alerts-cache tick. */
export interface AlertNotificationsLiveSlice {
  /** Recent alerts. Each entry is a `BellAlertItem` with `isRead`
   *  pre-computed so the bell dropdown can render the unread tint
   *  and envelope-toggle affordance without an extra lookup. */
  recent: BellAlertItem[];
  unreadCount: number;
  toasts: ToastItem[];
  /** Toasts that arrived while the stack was held, shown once it lets go
   *  (`toast-stack.ts`). */
  toastsWaiting: number;
}

/** Fields that change rarely (only on user action or storage
 *  events). Identity is stable across alerts-cache ticks. */
export interface AlertNotificationsStableSlice {
  markAllRead: () => void;
  markRead: (id: string) => void;
  markUnread: (id: string) => void;
  markAllUnread: () => void;
  dismissToast: (id: string) => void;
  muted: boolean;
  toggleMute: () => void;
  /**
   * Click-through handler for the bell dropdown + toast stack.
   *
   * For OPS it marks the alert as read, navigates to `alert.navigateTo`
   * (or the screen its subject lives on, or `/alerts`), and asks that
   * screen to open the matching drawer -- holding the request until the
   * screen has mounted (`pending-deep-link.ts`).
   *
   * For a SITE audience (FOH, `site_supervisor`, `service_operator`) it
   * is `showAlertWhere`: the Overview, never `/devices` or `/alerts`.
   * `/alerts` is RoleGuarded against FOH and `/devices` is not in their
   * nav; the place in the building is what the hotel's words point at.
   */
  handleAlertAction: (alert: AlertViewModel) => void;
  /**
   * Go to the Overview's Building view (`view=3d`, Decision 7), pointed at
   * `focus` (a locate link, `locate.ts`, pushed as a landing). Never opens
   * a drawer. False when no link can be built -- there is no active site
   * -- and nothing happened.
   */
  showWhere: (focus: LocateFocus, from?: LocateFrom | null) => boolean;
  /** "Show where" for an alert: marks it read, clears its toast, and
   *  lands on its place with `from=alert:<id>` (`show-where.ts`). */
  showAlertWhere: (alert: AlertViewModel) => void;
  /** While any toast holds (`ToastHold`), the whole stack holds still:
   *  nothing expires and new toasts wait (`toast-stack.ts`). */
  holdToast: (id: string, hold: ToastHold) => void;
  /** Ends one hold; the last one on the stack lets it go: the waiting
   *  toasts come in and every toast's TTL restarts. */
  releaseToast: (id: string, hold: ToastHold) => void;
  /** Set of alert ids marked read in the current session. */
  readSet: Set<string>;
  /** True when the provider has confirmed localStorage is
   *  accepting writes for this session. Flips to `false` after the
   *  first `setItem` throws (Safari Private, sandboxed iframes, …)
   *  and stays false for the lifetime of the page. Consumers can
   *  show a one-time info banner explaining that read state won't
   *  survive a refresh. */
  persistenceAvailable: boolean;
}

export interface AlertNotificationsContextValue
  extends AlertNotificationsLiveSlice,
    AlertNotificationsStableSlice {}

export const AlertNotificationsContext = createContext<AlertNotificationsContextValue | null>(null);
export const AlertNotificationsLiveContext = createContext<AlertNotificationsLiveSlice | null>(null);
export const AlertNotificationsStableContext = createContext<AlertNotificationsStableSlice | null>(null);